import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  type Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import {
  cloneElement,
  createContext,
  type HTMLProps,
  isValidElement,
  type ReactNode,
  use,
  useMemo,
  useState,
} from 'react';
import { Info } from '#icons/Info';
import { colors, spacing } from '#themes/stena-recycling';
import { TooltipBoundary, TooltipContentStyled, TooltipTargetWrapper } from './style';

export type TooltipOptions = {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function useTooltip({
  initialOpen = false,
  placement = 'top',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: TooltipOptions) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const { isMounted, styles } = useTransitionStyles(context);

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen === undefined,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen === undefined,
  });
  const dismiss = useDismiss(context, { referencePress: false, escapeKey: true });
  const click = useClick(context, {});
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([click, hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      isMounted,
      styles,
    }),
    [open, setOpen, interactions, data, isMounted, styles],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

const useTooltipContext = () => {
  const context = use(TooltipContext);

  if (!context) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

export function Tooltip({ children, ...options }: { children: ReactNode } & TooltipOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);

  return <TooltipContext value={tooltip}>{children}</TooltipContext>;
}

export function TooltipTrigger({
  children,
  ref: propRef,
  ...props
}: HTMLProps<HTMLElement> & { ref?: React.Ref<HTMLElement> }) {
  const context = useTooltipContext();
  const ref = useMergeRefs([
    (node) => {
      context.refs.setReference(node);
    },
    propRef,
  ]);

  if (isValidElement(children)) {
    const childProps = children.props as Record<string, unknown>;

    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...childProps,
        style: {
          ...(typeof childProps.style === 'object' && childProps.style !== null
            ? childProps.style
            : {}),
          cursor: 'pointer',
        },
        'data-state': context.open ? 'open' : 'closed',
      } as HTMLProps<HTMLElement> & { 'data-state': string }),
    );
  }

  return (
    <TooltipBoundary
      type="button"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <TooltipTargetWrapper
        ref={ref}
        data-state={context.open ? 'open' : 'closed'}
        {...context.getReferenceProps(props)}
      >
        <Info
          color={props.color ?? colors.primary.grey}
          width={spacing.small}
          height={spacing.small}
        />
      </TooltipTargetWrapper>
    </TooltipBoundary>
  );
}

export function TooltipContent({
  style,
  ref: propRef,
  ...props
}: HTMLProps<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
  const context = useTooltipContext();
  const ref = useMergeRefs([
    (node) => {
      context.refs.setFloating(node);
    },
    propRef,
  ]);

  if (!context.open) return null;

  return (
    <FloatingPortal>
      <TooltipContentStyled
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...style,
          ...context.styles,
        }}
        {...context.getFloatingProps(props)}
        isOpen={context.isMounted}
      />
    </FloatingPortal>
  );
}
